/**
 * ConnectorPreview Component
 *
 * Preview component for connector field type.
 * Shows a visual representation of a connector element.
 */

import React from 'react';
import { ConnectorPreviewProps } from './types';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ConnectorIcon } from 'assests/GmsIcons';

export const ConnectorPreview = ({
  config,
  isSelected,
  onClick,
  fieldName,
}: ConnectorPreviewProps) => {
  const { label, displayName, showTitle = true } = config;

  // Compute display label following existing UI model pattern
  const displayLabel = showTitle
    ? (displayName || label || fieldName || 'Unnamed Connector')
    : '';

  return (
    <div role="button"
      className={`tw-p-4 tw-border tw-rounded-lg tw-bg-surface tw-cursor-pointer
        tw-transition-all tw-duration-200 hover:tw-shadow-sm
        ${isSelected
          ? 'tw-border-accent tw-ring-2 tw-ring-accent-tint'
          : 'tw-border-edge hover:tw-border-edge-strong'
        }`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      data-testid="connector-field-preview"
    >
      {/* Header with field name, icon, and type badge */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-ink-secondary [&_path]:tw-stroke-current"><ConnectorIcon /></span>
          {showTitle ? (
            <Typography variant="sub-title-label" className="tw-text-ink">
              {displayLabel}
            </Typography>
          ) : (
            <Typography variant="sub-title-label" className="tw-text-ink-tertiary tw-italic">
              Hidden
            </Typography>
          )}
        </div>
        <span className="tw-text-[10px] tw-px-2 tw-py-0.5 tw-bg-surface-sunken tw-text-ink-secondary tw-rounded-full tw-uppercase tw-font-medium">
          Connector
        </span>
      </div>

      {/* Connector Visualization */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-py-2">
        {/* Source Node */}
        <div className="tw-flex tw-flex-col tw-items-center">
          <div className="tw-w-10 tw-h-10 tw-bg-accent-tint tw-border-2 tw-border-accent tw-rounded-lg tw-flex tw-items-center tw-justify-center">
            <svg className="tw-w-5 tw-h-5 tw-text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          </div>
          <span className="tw-text-xs tw-text-ink-secondary tw-mt-1">Source</span>
        </div>

        {/* Connection Line */}
        <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center">
          <div className="tw-relative tw-w-full tw-flex tw-items-center">
            <div className="tw-flex-1 tw-h-0.5 tw-bg-edge-strong" />
            <div className="tw-w-0 tw-h-0 tw-border-t-4 tw-border-t-transparent tw-border-b-4 tw-border-b-transparent tw-border-l-8 tw-border-l-edge-strong" />
          </div>
        </div>

        {/* Target Node */}
        <div className="tw-flex tw-flex-col tw-items-center">
          <div className="tw-w-10 tw-h-10 tw-bg-tone-success-tint tw-border-2 tw-border-tone-success-bold tw-rounded-lg tw-flex tw-items-center tw-justify-center">
            <svg className="tw-w-5 tw-h-5 tw-text-green-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <span className="tw-text-xs tw-text-ink-secondary tw-mt-1">Target</span>
        </div>
      </div>
    </div>
  );
};

export default ConnectorPreview;
